import { resolveSchemaTypes, toSchemaUrl } from './resolveSchemaTypes.js';
import { getListingUnitCount } from './listingInventory';

const ensureOpenGraphLocale = locale => {
  switch (locale) {
    case 'en':
      return 'en_US';
    default:
      return locale;
  }
};

/**
 * These will be used with Helmet <meta {...openGraphMetaProps} />
 */
export const openGraphMetaProps = data => {
  const {
    openGraphType,
    socialSharingTitle,
    socialSharingDescription,
    published,
    updated,
    url,
    locale,
    facebookImages,
    facebookAppId,
    marketplaceName,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && openGraphType && url && facebookImages)) {
    /* eslint-disable no-console */
    if (console && console.warn) {
      console.warn(
        `Can't create Open Graph meta tags:
        socialSharingTitle, socialSharingDescription, openGraphType, url, and facebookImages are needed.`
      );
    }
    /* eslint-enable no-console */
    return [];
  }

  const openGraphMeta = [
    { name: 'og:description', property: 'og:description', content: socialSharingDescription },
    { name: 'og:title', property: 'og:title', content: socialSharingTitle },
    { name: 'og:type', property: 'og:type', content: openGraphType },
    { name: 'og:url', property: 'og:url', content: url },
    { name: 'og:locale', property: 'og:locale', content: ensureOpenGraphLocale(locale) },
  ];

  if (facebookImages && facebookImages.length > 0) {
    facebookImages.forEach(i => {
      openGraphMeta.push({
        name:'og:image',
        property: 'og:image',
        content: i.url,
      });

      if (i.width && i.height) {
        openGraphMeta.push({name:'og:image:width', property:'og:image:width', content: i.width });
        openGraphMeta.push({name:'og:image:height', property:'og:image:height', content: i.height });
      }

      if (i.alt) {
        openGraphMeta.push({ name: 'og:image:alt', property: 'og:image:alt', content: i.alt });
      }
    });
  }

  if (marketplaceName) {
    openGraphMeta.push({ name:'og:site_name', property: 'og:site_name', content: marketplaceName });
  }

  if (facebookAppId) {
    openGraphMeta.push({name: 'fb:app_id', property: 'fb:app_id', content: facebookAppId });
  }

  if (published) {
    openGraphMeta.push({name:'article:published_time', property: 'article:published_time', content: published });
  }

  if (updated) {
    openGraphMeta.push({name:'article:modified_time', property: 'article:modified_time', content: updated });
  }

  return openGraphMeta;
};

/**
 * These will be used with Helmet <meta {...twitterMetaProps} />
 */
export const twitterMetaProps = data => {
  const {
    socialSharingTitle,
    socialSharingDescription,
    twitterHandle,
    twitterImages,
    url,
    marketplaceRootURL,
    siteTwitterHandle,
    imageAlt
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && url)) {
    /* eslint-disable no-console */
    if (console && console.warn) {
      console.warn(
        `Can't create twitter card meta tags:
        socialSharingTitle, socialSharingDescription, and url are needed.`
      );
    }
    /* eslint-enable no-console */
    return [];
  }

  const twitterMeta = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: socialSharingTitle },
    { name: 'twitter:description', content: socialSharingDescription },
    { name: 'twitter:url', content: url },
    { name: 'twitter:image:alt', content: imageAlt },
  ];

  if (siteTwitterHandle) {
    twitterMeta.push({ name: 'twitter:site', content: siteTwitterHandle });
  }

  if (twitterImages && twitterImages.length > 0) {
    twitterImages.forEach(i => {
      twitterMeta.push({
        name: 'twitter:image',
        content: i.url,
      });
    });
  }

  if (twitterHandle) {
    // TODO: If we want to connect providers twitter account on ListingPage
    // we needs to get this info among listing data (API support needed)
    twitterMeta.push({ name: 'twitter:creator', content: twitterHandle });
  }

  if (marketplaceRootURL) {
    twitterMeta.push({ name: 'twitter:domain', content: marketplaceRootURL });
  }

  return twitterMeta;
};

/**
 * These will be used with Helmet <meta {...metaTagProps} />
 * Creates data for Open Graph and Twitter meta tags.
 */
export const metaTagProps = (tagData, config) => {
  const {
    marketplaceRootURL,
    facebookAppId,
    marketplaceName,
    siteTwitterHandle,
    googleSearchConsole,
  } = config;

  const author = tagData.author || marketplaceName;
  const googleSiteVerification = googleSearchConsole?.googleSiteVerification;
  const googleSiteVerificationMaybe = googleSiteVerification
    ? [{ name: 'google-site-verification', content: googleSiteVerification }]
    : [];
  const defaultMeta = [
    { name: 'description', content: tagData.description },
    { name: 'author', content: author },
    ...googleSiteVerificationMaybe,
  ];

  const openGraphMeta = openGraphMetaProps({
    ...tagData,
    facebookAppId,
    marketplaceName,
  });

  const twitterMeta = twitterMetaProps({
    ...tagData,
    marketplaceRootURL,
    siteTwitterHandle,
  });

  return [...defaultMeta, ...openGraphMeta, ...twitterMeta];
};

// ─── Store & Listing SEO utilities ───────────────────────────────────────────

/**
 * Converts a store name to a URL-safe slug.
 * @param {string} storeName
 * @returns {string}
 */
export const generateStoreSlug = (storeName) => {
  if (typeof storeName !== 'string') return 'store';

  const slug = storeName
    .trim()
    .toLowerCase()
    .replace(/'/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug ? slug.slice(0, 60) : 'store';
};

/**
 * Converts a city name to a URL-safe slug for use in the store profile URL.
 *
 * Returns null (not a fallback string) when input is falsy — the caller uses
 * null to detect a missing city and omit the city segment from the URL entirely.
 *
 * @param {string} city - Raw city name from publicData
 * @returns {string|null} URL-safe slug, or null if city is absent
 */
const SPECIAL_CHARS = {
  'ł': 'l', 'Ł': 'l',
  'ø': 'o', 'Ø': 'o',
  'æ': 'ae', 'Æ': 'ae',
  'œ': 'oe', 'Œ': 'oe',
  'ß': 'ss',
  'ð': 'd', 'Ð': 'd',
  'þ': 'th', 'Þ': 'th',
  'ŋ': 'n', 'Ŋ': 'n',
  'ı': 'i',
};

export const generateCitySlug = (city) => {
  if (!city || typeof city !== 'string') return null;

  const slug = city
    // 1. Strip zero-width and invisible chars
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
    // 2. Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim()
    // 3. Explicit special character substitutions (before NFD)
    .replace(/[łŁøØæÆœŒßðÐþÞŋŊı]/g, (c) => SPECIAL_CHARS[c] || c)
    // 4. Lowercase
    .toLowerCase()
    // 5. NFD decomposition + strip combining diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // 6. Normalize dash variants to hyphen
    .replace(/[\u2013\u2014\u2011\u00AD]/g, '-')
    // 7. Strip apostrophes and quotes
    .replace(/[\u2018\u2019\u02BC''`"]/g, '')
    // 8. Replace anything non-alphanumeric with hyphen
    .replace(/[^a-z0-9]+/g, '-')
    // 9. Collapse multiple hyphens
    .replace(/-+/g, '-')
    // 10. Slice first, then trim edge hyphens
    .slice(0, 30)
    .replace(/^-+|-+$/g, '');

  return slug || null;
};

/**
 * Converts listing category to a URL-safe slug for listing URLs.
 *
 * @param {string} category - Raw category from listing publicData
 * @returns {string|null} URL-safe slug, or null if category is absent
 */
export const generateCategorySlug = category => {
  if (!category || typeof category !== 'string') return null;
  const slug = category
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || null;
};

/**
 * Strips HTML tags from a string and collapses whitespace.
 * @param {string} html
 * @returns {string}
 */
export const stripHtml = html => {
  if (html == null) return '';

  return html
    .replace(/[<＜][^>＞]*[>＞]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Parses intrinsic width and height from a Cloudinary URL that contains
 * transformation parameters such as `w_796,h_450` or `c_fill,w_796,h_450`.
 * Matches the first occurrence of w_NNN and h_NNN anywhere in the URL.
 * Safe to call with any string — returns null values when the pattern is absent.
 *
 * @param {string} url
 * @returns {{ width: number|null, height: number|null }}
 */
export const parseCloudinaryDimensions = (url) => {
  if (!url || typeof url !== 'string') return { width: null, height: null };
  const wMatch = url.match(/[,/]w_(\d+)/);
  const hMatch = url.match(/[,/]h_(\d+)/);
  return {
    width:  wMatch ? parseInt(wMatch[1], 10) : null,
    height: hMatch ? parseInt(hMatch[1], 10) : null,
  };
};

/**
 * Build responsive Cloudinary hero URLs (f_auto,q_auto + width) for LCP image + preload.
 * Non-Cloudinary URLs are returned unchanged for all slots.
 */
export const buildCloudinaryHeroImageSet = url => {
  if (!url || typeof url !== 'string') {
    return { hero800: null, hero1600: null, preloadUrl: null };
  }
  if (!url.includes('/upload/')) {
    return { hero800: url, hero1600: url, preloadUrl: url };
  }
  const inject = transform => url.replace('/upload/', `/upload/${transform}/`);
  return {
    hero800: inject('f_auto,q_auto,w_800'),
    hero1600: inject('f_auto,q_auto,w_1600'),
    preloadUrl: inject('f_auto,q_auto,w_1200'),
  };
};

/**
 * Converts a storeHours object to schema.org openingHours array format.
 * @param {object} storeHours
 * @returns {string[]|undefined}
 */
export const buildOpeningHours = storeHours => {
  if (!storeHours || typeof storeHours !== 'object') return undefined;

  const DAY_ABBR = {
    monday: 'Mo',
    tuesday: 'Tu',
    wednesday: 'We',
    thursday: 'Th',
    friday: 'Fr',
    saturday: 'Sa',
    sunday: 'Su',
  };

  const result = [];
  Object.keys(DAY_ABBR).forEach(day => {
    const dayData = storeHours[day];
    if (!dayData || !dayData.enabled || !dayData.timeSlots || !dayData.timeSlots.length) return;
    dayData.timeSlots.forEach(slot => {
      if (slot.openTime && slot.closeTime) {
        result.push(`${DAY_ABBR[day]} ${slot.openTime}-${slot.closeTime}`);
      }
    });
  });

  return result.length > 0 ? result : undefined;
};

const DAY_OF_WEEK_SCHEMA = {
  monday: 'https://schema.org/Monday',
  tuesday: 'https://schema.org/Tuesday',
  wednesday: 'https://schema.org/Wednesday',
  thursday: 'https://schema.org/Thursday',
  friday: 'https://schema.org/Friday',
  saturday: 'https://schema.org/Saturday',
  sunday: 'https://schema.org/Sunday',
};

const isValidHHMM = value => typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

/**
 * Converts a storeHours object to schema.org OpeningHoursSpecification array.
 * @param {object} storeHours
 * @returns {Array|undefined}
 */
export const buildOpeningHoursSpecification = storeHours => {
  if (!storeHours || typeof storeHours !== 'object') return undefined;

  const specs = [];

  Object.keys(DAY_OF_WEEK_SCHEMA).forEach(dayKey => {
    const dayData = storeHours[dayKey];
    const slots = Array.isArray(dayData?.timeSlots) ? dayData.timeSlots : [];
    if (!dayData?.enabled || slots.length === 0) return;

    slots.forEach(slot => {
      const opens = slot?.openTime;
      const closes = slot?.closeTime;
      if (!isValidHHMM(opens) || !isValidHHMM(closes)) return;

      specs.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: DAY_OF_WEEK_SCHEMA[dayKey],
        opens,
        closes,
      });
    });
  });

  return specs.length > 0 ? specs : undefined;
};

/**
 * Converts all-day unavailability exceptions to schema.org specialOpeningHoursSpecification.
 * Partial-day unavailability can't be represented accurately without deriving adjusted opening windows,
 * so we include only all-day closures here.
 *
 * @param {Array} exceptions
 * @returns {Array|undefined}
 */
export const buildSpecialOpeningHoursSpecification = exceptions => {
  if (!Array.isArray(exceptions) || exceptions.length === 0) return undefined;

  const specialSpecs = exceptions
    .filter(e => !!e?.allDay && typeof e?.date === 'string')
    .map(e => ({
      '@type': 'OpeningHoursSpecification',
      validFrom: e.date,
      validThrough: e.date,
      opens: '00:00',
      closes: '00:00',
    }));

  return specialSpecs.length > 0 ? specialSpecs : undefined;
};

const normalizeKeyword = value => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const collectCategoryKeywords = storeCategories => {
  if (!Array.isArray(storeCategories) || storeCategories.length === 0) return [];

  const seen = new Set();
  const keywords = [];

  const addKeyword = raw => {
    const keyword = normalizeKeyword(raw);
    if (!keyword) return;
    const dedupeKey = keyword.toLowerCase();
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    keywords.push(keyword);
  };

  storeCategories.forEach(category => {
    if (!category || typeof category !== 'object') {
      addKeyword(category);
      return;
    }

    addKeyword(category.name);

    if (category.names && typeof category.names === 'object') {
      Object.values(category.names).forEach(addKeyword);
    }
  });

  return keywords;
};

const toPositiveNumber = value => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
};

/**
 * Builds SEO-safe on-platform review stats from queried Sharetribe reviews.
 * Average rating is returned only when all reviews are loaded, to avoid
 * publishing an inaccurate aggregate in metadata/schema.
 *
 * @param {{ reviews?: Array, totalReviewCount?: number }} params
 * @returns {{ reviewCount: number, averageRating: number, hasExactAverage: boolean }}
 */
export const getOnPlatformReviewStats = ({ reviews = [], totalReviewCount } = {}) => {
  const validRatings = (Array.isArray(reviews) ? reviews : [])
    .map(r => Number(r?.attributes?.rating))
    .filter(rating => Number.isFinite(rating) && rating > 0);

  const loadedReviewCount = validRatings.length;
  const normalizedTotal = Math.floor(toPositiveNumber(totalReviewCount));
  const reviewCount = normalizedTotal > 0 ? normalizedTotal : loadedReviewCount;
  const hasExactAverage =
    loadedReviewCount > 0 &&
    (normalizedTotal === 0 || loadedReviewCount >= normalizedTotal);

  const averageRating = hasExactAverage
    ? Number(validRatings.reduce((sum, rating) => sum + rating, 0) / loadedReviewCount).toFixed(1)
    : 0;
  return { reviewCount, averageRating, hasExactAverage };
};

/**
 * Builds a ProfilePage + Organization schema object for a store.
 * mainEntity is typed as Organization (not a LocalBusiness subtype) per
 * Google's Profile Page structured data requirements; the category-specific
 * business type is still exposed via mainEntity.additionalType.
 * @param {{
 *   provider: object,
 *   storeUrl: string,
 *   rootUrl: string,
 *   hasBlogPosts?: boolean,
 *   averageRating?: number,
 *   reviewCount?: number
 * }} params
 * @returns {object}
 */
export const buildStoreSchema = ({
  provider,
  storeUrl,
  rootUrl,
  hasBlogPosts = false,
  averageRating = 0,
  reviewCount = 0,
}) => {
  if (!provider) return null;

  const profile = provider.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const metadata = profile.metadata || {};
  const SAMEAS_FIELDS = ['websiteUrl', 'instagramUrl', 'facebookUrl', 'linkedinUrl', 'twitterUrl'];
  const sameAsUrls = SAMEAS_FIELDS
    .map(key => metadata[key])
    .filter(url => typeof url === 'string' && url.trim().length > 0);
  const displayName = profile.displayName;

  const storeName = publicData.storeName || displayName;
  const description = stripHtml(publicData.storeAbout) || undefined;
  const storeAddress = publicData.storeAddress || {};
  const geoOrigin = storeAddress.origin;
  const geo =
    geoOrigin &&
    typeof geoOrigin.lat === 'number' &&
    typeof geoOrigin.lng === 'number'
      ? {
          '@type':   'GeoCoordinates',
          latitude:   geoOrigin.lat,
          longitude:  geoOrigin.lng,
        }
      : undefined;
  const city = storeAddress.city || publicData.city || undefined;
  const state = storeAddress.state || publicData.state || undefined;
  const country = storeAddress.country || publicData.country || undefined;
  const postcode = storeAddress.postcode || publicData.postcode || undefined;
  const streetAddress = storeAddress.address || publicData.address || undefined;
  const storeHours = publicData.storeHours;
  const exceptions = publicData.exceptions;
  const storeCategories = publicData.storeCategories;
  const topCategory = Array.isArray(storeCategories) && storeCategories.length > 0
    ? storeCategories.reduce((best, cat) => {
        const bestOrder = typeof best?.order === 'number' ? best.order : Infinity;
        const catOrder  = typeof cat?.order  === 'number' ? cat.order  : Infinity;
        return catOrder < bestOrder ? cat : best;
      }, null)
    : null;
  const areaServed = city ? { "@type": "City", "name": city } : null
  const totalListings = publicData.totalListings;
  const rawCreatedAt = provider?.attributes?.createdAt;
  // console.log('rawCreatedAt :>> ', provider);
  const foundingDate = rawCreatedAt
    ? (typeof rawCreatedAt === 'string'
        ? rawCreatedAt.slice(0, 10)
        : rawCreatedAt instanceof Date
          ? rawCreatedAt.toISOString().slice(0, 10)
          : undefined)
    : undefined;

  const profileImageData = publicData.profileImage;
  const profileImageUrl = profileImageData?.secure_url
    || (publicData.storeMedia || []).find(m => m.type === 'photo')?.secure_url
    || undefined;

  const storeDisplayPhoto = publicData.storeDisplayPhoto;
  const storeDisplayCaption = storeDisplayPhoto?.seoTags?.caption || '';
  const storeImageObject = storeDisplayPhoto?.secure_url
    ? {
        '@type':    'ImageObject',
        url:         storeDisplayPhoto.secure_url,
        contentUrl:  storeDisplayPhoto.secure_url,
        description: storeDisplayPhoto?.seoTags?.altText || storeName || '',
        ...(storeDisplayCaption ? { caption: storeDisplayCaption } : {}),
        width:       240,
        height:      240,
      }
    : undefined;

  const storeCoverImage = publicData.storeCoverImage;
  const storeCoverCaption = storeCoverImage?.seoTags?.caption || '';
  const storeCoverImageObject = storeCoverImage?.secure_url
    ? {
        '@type':    'ImageObject',
        url:         storeCoverImage.secure_url,
        contentUrl:  storeCoverImage.secure_url,
        description: storeCoverImage?.seoTags?.altText || storeName || '',
        ...(storeCoverCaption ? { caption: storeCoverCaption } : {}),
        width:       storeCoverImage?.width || 1400,
        height:      storeCoverImage?.height || 400,
      }
    : undefined;

  const storeImagesObject = [ storeImageObject, storeCoverImageObject ].filter(Boolean);

    const logoCaption = profileImageData?.seoTags?.caption || '';
    const logoImageObject = profileImageData?.secure_url
    ? {
        '@type':     'ImageObject',
        url:          profileImageData.secure_url,
        width:        200,
        height:       200,
        name:         profileImageData.seoTags?.fileName || storeName || '',
        description:  profileImageData.seoTags?.altText  || `${storeName} store logo`,
        ...(logoCaption ? { caption: logoCaption } : {}),
      }
    : undefined;

  const { primary: businessType, additional: additionalTypes } = resolveSchemaTypes(storeCategories);
  // Google's Profile Page structured data requires mainEntity to be exactly
  // Person or Organization — subtypes (LocalBusiness, PetStore, etc.) are
  // rejected by Search Console ("Invalid object type for field 'mainEntity'").
  // The category-specific type is preserved via additionalType instead.
  const mainEntityAdditionalTypes = Array.from(
    new Set([toSchemaUrl(businessType), ...additionalTypes])
  );
  const categoryKeywords = collectCategoryKeywords(storeCategories);
  const openingHours = buildOpeningHours(storeHours);
  const openingHoursSpecification = buildOpeningHoursSpecification(storeHours);
  const specialOpeningHoursSpecification = buildSpecialOpeningHoursSpecification(exceptions);

  const addressFields = {};
  if (streetAddress) addressFields.streetAddress = streetAddress;
  if (city) addressFields.addressLocality = city;
  if (state) addressFields.addressRegion = state;
  if (postcode) addressFields.postalCode = postcode;
  if (country) addressFields.addressCountry = country;
  const address = Object.keys(addressFields).length > 0
    ? { '@type': 'PostalAddress', ...addressFields }
    : undefined;

  const normalizedAverageRating = toPositiveNumber(averageRating);
  const normalizedReviewCount = Math.floor(toPositiveNumber(reviewCount));

  const aggregateRating =
    normalizedAverageRating > 0 && normalizedReviewCount > 0
      ? {
          '@type': 'AggregateRating',
          ratingValue: Math.round(normalizedAverageRating * 10) / 10,
          reviewCount: normalizedReviewCount,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined;

  const hasPart = buildHasPartSchema({
    storeBaseUrl: storeUrl,
    publicData,
    hasBlogPosts,
    reviewCount: normalizedReviewCount,
  });

  const mainEntity = {
    '@type': 'Organization',
    '@id': `${storeUrl}#local-business`,
    additionalType: mainEntityAdditionalTypes,
    ...(storeName ? { name: storeName } : {}),
    ...(description ? { description } : {}),
    ...(storeUrl ? { url: storeUrl } : {}),
    ...(logoImageObject ? { logo: logoImageObject } : {}),
    ...(storeImagesObject.length ? { image: storeImagesObject } : {}),
    ...(sameAsUrls.length > 0 && { sameAs: sameAsUrls }),
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
    ...(areaServed ? { areaServed } : {}),
    ...(foundingDate ? { foundingDate } : {}),
    ...(aggregateRating ? { aggregateRating } : {}),
    ...(categoryKeywords.length > 0 ? { knowsAbout: categoryKeywords } : {}),
    ...(openingHours ? { openingHours } : {}),
    ...(openingHoursSpecification ? { openingHoursSpecification } : {}),
    ...(specialOpeningHoursSpecification ? { specialOpeningHoursSpecification } : {}),
    ...(totalListings > 0 ? { numberOfItems: totalListings } : {}),
    ...(hasPart.length > 0 ? { hasPart } : {}),
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${storeUrl}#profile-page`,
    ...(storeUrl ? { url: storeUrl } : {}),
    mainEntity,
  };
};

/**
 * Builds a FAQPage schema object from an array of FAQ entries.
 * @param {Array<{question: string, answer: string}>} faqs
 * @returns {object|null}
 */
export const buildFaqSchema = faqs => {
  if (!faqs || !faqs.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Builds an ImageGallery schema object for a store's media gallery.
 * Photos become ImageObject entries; videos become VideoObject entries.
 * Cloudinary video thumbnails are derived from the upload URL automatically.
 *
 * @param {{ storeMedia: Array, storeName: string, galleryUrl: string }} params
 * @returns {object|null}
 */
export const buildGallerySchema = ({ storeMedia, storeName, galleryUrl }) => {
  if (!Array.isArray(storeMedia) || storeMedia.length === 0) return null;

  const name = storeName || 'Store';
  let imgIdx = 0;
  let vidIdx = 0;

  const associatedMedia = storeMedia
    .filter(m => m?.secure_url || m?.playback_url)
    .map(m => {
      if (m.type === 'video') {
        const contentUrl = m.secure_url || m.playback_url;
        const thumbnailUrl =
          contentUrl && contentUrl.includes('/video/upload/')
            ? contentUrl
                .replace('/video/upload/', '/video/upload/w_800,so_1,f_jpg/')
                .replace(/\.[^.]+$/, '.jpg')
            : undefined;
        const uploadDate = m.createdAt
          ? (typeof m.createdAt === 'string'
              ? m.createdAt
              : new Date(m.createdAt).toISOString())
          : undefined;
        const durationISO = (() => {
          const secs = m.duration;
          if (typeof secs !== 'number' || secs <= 0) return undefined;
          const totalSeconds = Math.round(secs);
          const hh = Math.floor(totalSeconds / 3600);
          const mm = Math.floor((totalSeconds % 3600) / 60);
          const ss = totalSeconds % 60;
          return `PT${hh > 0 ? `${hh}H` : ''}${mm > 0 ? `${mm}M` : ''}${ss > 0 ? `${ss}S` : '0S'}`;
        })();
        return {
          '@type':      'VideoObject',
          name:          m.seoTags?.fileName || `${name} gallery video ${++vidIdx}`,
          description:   m.seoTags?.altText  || `Video from ${name}'s rental store gallery`,
          ...(m.seoTags?.caption ? { caption:      m.seoTags.caption } : {}),
          contentUrl,
          ...(thumbnailUrl   ? { thumbnailUrl }              : {}),
          ...(uploadDate     ? { uploadDate }                : {}),
          ...(durationISO    ? { duration: durationISO }     : {}),
          ...(m.width        ? { width:  m.width  }          : {}),
          ...(m.height       ? { height: m.height }          : {}),
        };
      }
      return {
        '@type': 'ImageObject',
        name: m.seoTags?.fileName || `${name} gallery photo ${++imgIdx}`,
        description: m.seoTags?.altText || `Photo from ${name}'s rental store gallery`,
        ...(m.seoTags?.caption ? { caption: m.seoTags.caption } : {}),
        contentUrl: m.secure_url,
        url: m.secure_url,
        ...(m.width && m.height ? { width: m.width, height: m.height } : {}),
      };
    });

  if (associatedMedia.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    '@id': galleryUrl ? `${galleryUrl}#image-gallery` : undefined,
    url: galleryUrl,
    name: `${name} Gallery`,
    description: `Photos and videos from ${name}'s rental store`,
    associatedMedia,
  };
};

/**
 * Maps a Sharetribe listing state to a schema.org availability URL.
 * @param {string} listingState
 * @returns {string|null}
 */
export const getSchemaAvailability = listingState => {
  if (listingState === 'closed') return 'https://schema.org/OutOfStock';
  if (listingState === 'draft') return null;
  return 'https://schema.org/InStock';
};

/**
 * Builds a Product schema object for a rental listing.
 * @param {{ listing: object, canonicalUrl: string, rootUrl: string }} params
 * @returns {object}
 */
export const buildProductSchema = ({ listing, canonicalUrl, rootUrl, defaultPriceKey, displayPrice, averageRating, reviewCount, blogs }) => {
  if (!listing) return null;

  const attrs = listing.attributes || {};
  const profile = listing.author?.attributes?.profile || {};
  const displayName = profile.displayName;
  const authorPublicData = profile.publicData || {};
  const sellerName = authorPublicData.storeName || displayName;

  const authorUserId = listing.author?.id?.uuid;
  const authorCity = authorPublicData?.storeAddress?.city || authorPublicData?.city || null;

  // Build store base URL and seller @id to cross-reference the store's LocalBusiness entity.
  // Pattern matches buildStoreSchema @id: ${storeUrl}#local-business
  const storeBase = (rootUrl && authorUserId && sellerName)
    ? (() => {
        const slug = sellerName
          .toLowerCase()
          .replace(/'/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60);
        const citySlug = authorCity
          ? (() => {
              const s = authorCity
                .toLowerCase()
                .replace(/['']/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-+|-+$/g, '')
                .slice(0, 30);
              return s || null;
            })()
          : null;
        return citySlug
          ? `${rootUrl}/rental-owner/${citySlug}/${slug}/${authorUserId}`
          : `${rootUrl}/rental-owner/${slug}/${authorUserId}`;
      })()
    : null;
  const sellerAtId = storeBase ? `${storeBase}#local-business` : undefined;

  const seller = sellerName
    ? {
        '@type': 'Organization',
        name: sellerName,
        ...(sellerAtId ? { '@id': sellerAtId } : {}),
      }
    : undefined;

  const sku = listing.id?.uuid || undefined;

  const brand = seller
    ? {
        '@type': 'Brand',
        name:    seller.name,
        ...(sellerAtId ? { '@id': sellerAtId } : {}),
      }
    : undefined;

  const rawCategory = attrs?.publicData?.category;
  const category = rawCategory
    ? rawCategory.replace(/([A-Z])/g, ' $1').trim()
    : undefined;

  const listingTitle = attrs?.title;
  const keywords = listingTitle
    ? `${listingTitle} rental`
    : undefined;

  const createdAt = attrs.createdAt;

  // fallback chain
  const publishedAt = attrs.publicData?.publishedAt || createdAt;
  const updatedAt = attrs.publicData?.updatedAt || createdAt;

  // small guard (cheap insurance)
  const isValidISO = (date) => {
    return typeof date === "string" && !isNaN(Date.parse(date));
  };

  const datePublished = isValidISO(publishedAt) ? publishedAt : createdAt;
  const dateModified = isValidISO(updatedAt) ? updatedAt : createdAt;

  const availability = getSchemaAvailability(attrs.state);
  // Use displayPrice (the same Money object used for title/meta) when provided,
  // falling back to the listing's raw price. This keeps schema and meta in sync.
  const priceSource = displayPrice || attrs.price;
  const price = priceSource ? priceSource.amount / 100 : undefined;
  const priceCurrency = priceSource?.currency;

  // Maps each price package key to the schema.org UN/CEFACT unit code that matches
  // what getDefaultDailyPrice actually returns for that key:
  //   - All day-based packages are normalized to a daily rate → 'DAY'
  //   - perMinutePrice is normalized to an hourly equivalent (amount × 60) → 'HUR'
  //   - perHourPrice is returned as-is → 'HUR'
  const PRICE_KEY_TO_UNIT = {
    perDayPrice: 'DAY',
    overnightPrice: 'DAY',
    twoDaysPrice: 'DAY',
    threeDaysPrice: 'DAY',
    fourDaysPrice: 'DAY',
    fiveDaysPrice: 'DAY',
    oneWeekPrice: 'DAY',
    twentyEightDaysPrice: 'DAY',
    perHourPrice: 'HUR',
    perMinutePrice: 'HUR',
  };
  const unitCode = PRICE_KEY_TO_UNIT[defaultPriceKey] || undefined;
  const priceSpecification = (unitCode && price != null && priceCurrency)
    ? {
        '@type': 'UnitPriceSpecification',
        price,
        priceCurrency,
        unitCode,
      }
    : undefined;

  const publicDataImages = attrs?.publicData?.listingImages || [];
  const images =
    publicDataImages.length > 0
      ? publicDataImages
          .filter(img => img.secure_url)
          .map(img => {
            const sharetribeImageUrl = Array.isArray(listing?.images)
              ? listing.images.find(
                  elm => elm?.id?.uuid === img.sharetribeImgId
                )?.attributes?.variants?.['scaled-xlarge']?.url
              : false;
            return {
              '@type': 'ImageObject',
              url: sharetribeImageUrl || img.secure_url,
              contentUrl: sharetribeImageUrl || img.secure_url,
              name: img.seoTags?.fileName || '',
              description: img.seoTags?.altText || '',
              caption: img.seoTags?.caption || '',
              ...(img.width && img.height
                ? { width: img.width, height: img.height }
                : {}),
            };
          })
      : (listing.images || [])
          .map(img => img?.attributes?.variants?.['scaled-xlarge']?.url)
          .filter(Boolean);

  // Total units the owner has (publicData.quantity), as schema.org inventoryLevel so search
  // engines and AI assistants can read it. Total stock only — the marketplace has no
  // reliable per-date availability, so nothing date-specific is claimed here. Present on
  // every listing: "1" is as much an answer as "12".
  const unitCount = getListingUnitCount(attrs?.publicData);
  const inventoryLevel = { '@type': 'QuantitativeValue', value: unitCount, unitText: 'units' };

  const offers = {
    '@type': 'Offer',
    ...(canonicalUrl ? { url: canonicalUrl } : {}),
    ...(price != null ? { price } : {}),
    ...(priceCurrency ? { priceCurrency } : {}),
    ...(priceSpecification ? { priceSpecification } : {}),
    ...(availability ? { availability } : {}),
    inventoryLevel,
    ...(seller ? { seller } : {}),
  };

  // Guard: aggregateRating must only reflect on-platform Sharetribe reviews.
  // External ratings (e.g. online_review_summary) must never be passed here.
  // Omit the field entirely when there are zero on-platform reviews.
  const normalizedAverageRating = Number(averageRating);
  const normalizedReviewCount = Number(reviewCount);
  const hasAggregateRating =
    Number.isFinite(normalizedAverageRating) &&
    normalizedAverageRating > 0 &&
    Number.isFinite(normalizedReviewCount) &&
    normalizedReviewCount > 0;

  const aggregateRating = hasAggregateRating
    ? {
        '@type': 'AggregateRating',
        ratingValue: Math.round(normalizedAverageRating * 10) / 10,
        reviewCount: Math.floor(normalizedReviewCount),
        bestRating: 5,
        worstRating: 1,
      }
    : undefined;

  const publishedBlogs = Array.isArray(blogs)
    ? blogs.filter(b => b && b.status === 'published')
    : [];

  const subjectOf = publishedBlogs.length > 0
    ? publishedBlogs.map(blog => {
        const blogUrl = storeBase ? `${storeBase}/blog/${blog._id}` : null;
        const excerpt = blog.content?.excerpt ? stripHtml(blog.content.excerpt) : '';
        const datePublishedBlog = blog.createdAt instanceof Date
          ? blog.createdAt.toISOString()
          : blog.createdAt;

        return {
          '@type': 'BlogPosting',
          ...(blogUrl ? { '@id': `${blogUrl}#blog-post` } : {}),
          headline: blog.title,
          ...(excerpt ? { description: excerpt } : {}),
          ...(datePublishedBlog ? { datePublished: datePublishedBlog } : {}),
          ...(blog.image?.secure_url ? {
            image: {
              '@type': 'ImageObject',
              url: blog.image.secure_url,
              ...(blog.image.width != null ? { width: blog.image.width } : {}),
              ...(blog.image.height != null ? { height: blog.image.height } : {}),
            },
          } : {}),
          ...(sellerName ? {
            author: {
              '@type': 'Organization',
              name: sellerName,
              ...(sellerAtId ? { '@id': sellerAtId } : {}),
            },
          } : {}),
          ...(canonicalUrl ? {
            isPartOf: {
              '@type': 'Product',
              '@id': `${canonicalUrl}#product`,
            },
          } : {}),
          ...(blog.label ? { keywords: blog.label } : {}),
        };
      })
    : null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    ...(canonicalUrl ? { '@id': `${canonicalUrl}#product` } : {}),
    ...(attrs.title ? { name: attrs.title } : {}),
    ...((() => {
      const schemaDescription = attrs.description ? stripHtml(attrs.description) : '';
      return schemaDescription ? { description: schemaDescription } : {};
    })()),
    ...(canonicalUrl ? { url: canonicalUrl } : {}),
    ...(sku ? { sku } : {}),
    ...(brand ? { brand } : {}),
    ...(category ? { category } : {}),
    ...(keywords ? { keywords } : {}),
    ...(images.length > 0 ? { image: images } : {}),

    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    offers,
    ...(aggregateRating ? { aggregateRating } : {}),
    ...(subjectOf ? { subjectOf } : {}),
  };
};

/**
 * Builds a BreadcrumbList schema object.
 * @param {Array<{name: string, url: string}>} items
 * @returns {object}
 */
export const buildBreadcrumbSchema = items => {
  if (!items || !items.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * Builds a canonical URL for a search page, keeping only intent-relevant params.
 * @param {{ locationSearch: string, rootUrl: string }} params
 * @returns {string}
 */
export const buildSearchCanonical = ({ locationSearch, rootUrl }) => {
  // iOS Safari's share button reads <link rel="canonical">, so every param that
  // represents search intent must be preserved here. Omitting a param means it
  // is silently dropped from the shared URL.
  const KEEP_PARAMS = [
    // Location
    'address', 'bounds', 'city', 'origin', 'country',
    // Search term
    'keyword',
    // Taxonomy / filters
    'category',
    'access_to_rentals',
    'Condition_of_the_item',
    'Trust_and_verification',
    'cancelation_policy',
    'reason_for_listing',
    'security_deposit',
    'priceRange',
    // Sort
    'sort',
  ];
  const params = new URLSearchParams(locationSearch || '');
  const kept = new URLSearchParams();

  KEEP_PARAMS.forEach(key => {
    const val = params.get(key);
    if (val) kept.set(key, val);
  });

  const qs = kept.toString();
  return `${rootUrl || ''}/search${qs ? `?${qs}` : ''}`;
};

/**
 * Returns true if the search URL has real intent signals worth indexing.
 * @param {{ locationSearch: string }} params
 * @returns {boolean}
 */
export const shouldIndexSearchPage = ({ locationSearch }) => {
  const params = new URLSearchParams(locationSearch || '');

  if (params.has('mapSearch')) return false;

  const page = params.get('page');
  if (page && parseInt(page, 10) > 1) return false;

  const NOISE_PARAMS = new Set(['bounds', 'mapSearch', 'page']);
  if (params.has('address')) return true;

  for (const key of params.keys()) {
    if (!NOISE_PARAMS.has(key)) return true;
  }

  return true;
};

/**
 * Builds an intent-aware page title for the search page.
 * @param {{ filterParams: object, marketplaceName: string }} params
 * @returns {string}
 */
export const buildSearchTitle = ({ filterParams, marketplaceName }) => {
  const address = filterParams?.address || '';
  const category = filterParams?.category
    ? filterParams.category.charAt(0).toUpperCase() + filterParams.category.slice(1)
    : '';

  if (address && category) return `${category} Rentals in ${address} | ${marketplaceName}`;
  if (address) return `Rentals in ${address} | ${marketplaceName}`;
  if (category) return `${category} Rentals | ${marketplaceName}`;
  return `Search Rentals | ${marketplaceName}`;
};

/**
 * Builds an intent-aware meta description for the search page.
 * @param {{ filterParams: object, marketplaceName: string }} params
 * @returns {string}
 */
export const buildSearchDescription = ({ filterParams, marketplaceName }) => {
  const address = filterParams?.address || '';
  const category = filterParams?.category
    ? filterParams.category.charAt(0).toUpperCase() + filterParams.category.slice(1)
    : '';

  if (address && category) return `Browse ${category} rentals in ${address}. Find and book on ${marketplaceName}.`;
  if (address) return `Browse rental listings in ${address} on ${marketplaceName}.`;
  if (category) return `Browse ${category} rentals on ${marketplaceName}.`;
  return `Search rental listings on ${marketplaceName}.`;
};

/**
 * Constructs the stable canonical URL for a store profile page or tab sub-route.
 *
 * URL pattern with city (preferred):
 *   {rootUrl}/rental-owner/{citySlug}/{storeSlug}/{userId}[/{tab}]
 *
 * URL pattern without city (when city is null/empty):
 *   {rootUrl}/rental-owner/{storeSlug}/{userId}[/{tab}]
 *
 * @param {object} params
 * @param {string} params.rootUrl       - Marketplace root URL (no trailing slash)
 * @param {string} params.storeName     - Raw store name from publicData
 * @param {string|null} params.city     - Raw city from publicData, or null
 * @param {string} params.userId        - Store owner UUID
 * @param {string} params.activeTab     - Active tab name or 'home'
 * @returns {string} Canonical URL
 */
export const buildStoreCanonicalUrl = ({ rootUrl, storeName, city, userId, activeTab }) => {
  const storeSlug = generateStoreSlug(storeName);
  const citySlug = generateCitySlug(city);

  // Include city segment only when city resolves to a valid slug
  const base = citySlug
    ? `${rootUrl}/rental-owner/${citySlug}/${storeSlug}/${userId}`
    : `${rootUrl}/rental-owner/${storeSlug}/${userId}`;

  const TAB_PATHS = {
    about:   '/about',
    reviews: '/reviews',
    gallery: '/gallery',
    faqs:    '/faqs',
    blog:    '/blog',
  };

  return base + (TAB_PATHS[activeTab] || '');
};

/**
 * Builds SearchResultsPage schema for the Algolia search page.
 * listings are Algolia hit objects — field names differ from SDK objects.
 * Algolia hits use objectID for ID and attributes.title for title.
 * @param {{ title: string, description: string, canonicalUrl: string, listings: Array, rootUrl: string }} params
 * @returns {object}
 */
export const createSearchResultSchema = ({ title, description, canonicalUrl, listings = [], rootUrl }) => ({
  '@context': 'https://schema.org',
  '@type': 'SearchResultsPage',
  name: title,
  description,
  url: canonicalUrl,
  ...(listings.length > 0 && {
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: listings.length,
      itemListElement: listings.slice(0, 10).map((listing, i) => {
        // Keep schema listing URLs aligned with canonical listing route shape.
        // Prefer city+category, then city-only, then fallback to slug+id.
        const listingSlug =
          (listing.attributes?.title || '').toLowerCase().replace(/\s+/g, '-') || 'listing';
        const listingId = listing.objectID || '';
        const listingCitySlug = generateCitySlug(listing.attributes?.publicData?.city);
        const listingCategorySlug = generateCategorySlug(listing.attributes?.publicData?.category);

        const listingPath =
          listingCitySlug && listingCategorySlug
            ? `/rentals/${listingCitySlug}/${listingCategorySlug}/${listingSlug}/${listingId}`
            : listingCitySlug
              ? `/rentals/${listingCitySlug}/${listingSlug}/${listingId}`
              : `/rentals/${listingSlug}/${listingId}`;

        return {
          '@type': 'ListItem',
          position: i + 1,
          url: `${rootUrl}${listingPath}`,
          name: listing.attributes?.title || '',
        };
      }),
    },
  }),
});

// ─── Store tab SEO utilities ────────────────────────────────────────────────

const TAB_DISPLAY_TO_SEGMENT = {
  About: 'about',
  Reviews: 'reviews',
  Gallery: 'gallery',
  FAQs: 'faqs',
  Blog: 'blog',
};

/**
 * Builds the hasPart array for the LocalBusiness mainEntity.
 * Only includes tab entries whose content actually exists.
 *
 * @param {{ storeBaseUrl: string, publicData: object, hasBlogPosts?: boolean, reviewCount?: number }} params
 * @returns {Array}
 */
export const buildHasPartSchema = ({
  storeBaseUrl,
  publicData,
  hasBlogPosts = false,
  reviewCount = 0,
} = {}) => {
  if (!storeBaseUrl || !publicData) return [];

  const parts = [];

  if (publicData.storeAbout) {
    parts.push({ '@type': 'WebPage', name: 'About', url: `${storeBaseUrl}/about` });
  }

  if (toPositiveNumber(reviewCount) > 0) {
    parts.push({ '@type': 'WebPage', name: 'Reviews', url: `${storeBaseUrl}/reviews` });
  }

  if (Array.isArray(publicData.storeFaqs) && publicData.storeFaqs.length > 0) {
    parts.push({ '@type': 'WebPage', name: 'FAQs', url: `${storeBaseUrl}/faqs` });
  }

  if (Array.isArray(publicData.storeMedia) && publicData.storeMedia.length > 0) {
    parts.push({ '@type': 'WebPage', name: 'Gallery', url: `${storeBaseUrl}/gallery` });
  }

  if (hasBlogPosts === true) {
    parts.push({ '@type': 'WebPage', name: 'Blog', url: `${storeBaseUrl}/blog` });
  }

  return parts;
};

/**
 * Builds a BreadcrumbList schema for a store tab page.
 *
 * @param {{ rootUrl: string, storeBaseUrl: string, storeName: string, tabName: string }} params
 * @returns {object|null}
 */
export const buildTabBreadcrumbSchema = ({ rootUrl, storeBaseUrl, storeName, tabName } = {}) => {
  if (!rootUrl || !storeBaseUrl || !storeName || !tabName) return null;

  let marketplaceName;
  try {
    marketplaceName = new URL(rootUrl).hostname.replace(/^www\./, '');
  } catch {
    marketplaceName = rootUrl;
  }

  const segment = TAB_DISPLAY_TO_SEGMENT[tabName] || tabName.toLowerCase();
  const tabUrl = `${storeBaseUrl}/${segment}`;

  return buildBreadcrumbSchema([
    { name: marketplaceName, url: rootUrl },
    { name: storeName, url: storeBaseUrl },
    { name: tabName, url: tabUrl },
  ]);
};

/**
 * Returns the robots directive for a store tab based on content availability.
 *
 * @param {{ activeTab: string, publicData: object, hasBlogPosts?: boolean, reviewCount?: number }} params
 * @returns {string}
 */
export const buildTabRobotsDirective = ({
  activeTab,
  publicData,
  hasBlogPosts = false,
  reviewCount = 0,
} = {}) => {
  if (!publicData) return 'index,follow';

  switch (activeTab) {
    case 'home':
      return 'index,follow';
    case 'about':
      return stripHtml(publicData.storeAbout).length >= 150
        ? 'index,follow'
        : 'noindex,follow';
    case 'reviews':
      return toPositiveNumber(reviewCount) > 0
        ? 'index,follow'
        : 'noindex,follow';
    case 'gallery':
      return publicData.storeMedia?.length > 0
        ? 'index,follow'
        : 'noindex,follow';
    case 'faqs':
      return publicData.storeFaqs?.length > 0
        ? 'index,follow'
        : 'noindex,follow';
    case 'blog':
      return hasBlogPosts === true
        ? 'index,follow'
        : 'noindex,follow';
    default:
      return 'index,follow';
  }
};
